/* James Crossing — fabric quantity calculator (upholstery, Roman shade, drapery).
   Reuses the theme's <modal-dialog> and cart store, like wallpaper-estimator.js.
   All lengths are handled internally in centimetres. Results are whole metres (the unit sold), yards in brackets. */
import Cart from "cart-store";

/* ---------- parsing (pure) ---------- */
const CM_PER_IN = 2.54, M_PER_YD = 0.9144;
const EPS = 1e-9;
// Remove imperial values in brackets: "145 cm (57 in)" -> "145 cm". Keeps "(large scale)".
function stripImperial(s) {
  return String(s || "")
    .replace(/\(\s*[\d.,]+\s*(?:in|inch|inches|ft|feet|yd|yds|yards?|"|”|″|'')\.?\s*\)/gi, "")
    .replace(/\s+/g, " ").trim();
}
function num(x) { return parseFloat(String(x).replace(",", ".")); }
function parseCm(s) {
  const t = stripImperial(s).toLowerCase();
  let m = t.match(/(\d+(?:[.,]\d+)?)\s*cm/); if (m) return num(m[1]);
  m = t.match(/(\d+(?:[.,]\d+)?)\s*mm\b/); if (m) return num(m[1]) / 10;
  m = t.match(/(\d+(?:[.,]\d+)?)\s*m\b/); if (m) return num(m[1]) * 100;
  m = t.match(/^(\d+(?:[.,]\d+)?)$/); if (m) return num(m[1]);
  m = String(s || "").toLowerCase().match(/(\d+(?:[.,]\d+)?)\s*(?:in\b|inch|"|”|″)/); if (m) return num(m[1]) * CM_PER_IN;
  return null;
}
function parseWidth(s) {
  const t = stripImperial(s);
  const pw = t.match(/printed width[^0-9]*(\d+(?:[.,]\d+)?)\s*cm/i);
  if (pw) return { cm: num(pw[1]), printed: true };
  const v = parseCm(t);
  return v && v > 20 ? { cm: v, printed: false } : null;
}
function parseRepeat(s, scale) {
  const raw = String(s || "").trim();
  if (!raw) return { cm: 0, status: "none" };
  if (/^(tbc|tba|tbd|n\/?a|unknown|-+)$/i.test(raw)) return { cm: 0, status: "tbc" };
  if (/no\s*repeat|^none$|random|^plain$/i.test(raw)) return { cm: 0, status: "none" };
  const t = stripImperial(raw);
  const halfDrop = /drop/i.test(t);
  const parts = t.split("/").map((p) => ({ cm: parseCm(p), large: /large/i.test(p), small: /small/i.test(p) })).filter((p) => p.cm != null && p.cm >= 0);
  if (!parts.length) return { cm: 0, status: "unreadable" };
  const scaled = parts.some((p) => p.large || p.small);
  let pick = null;
  if (scaled && scale) { const sc = String(scale).toLowerCase(); pick = parts.find((p) => (sc.includes("large") && p.large) || (sc.includes("small") && p.small)); }
  if (!pick) pick = parts.reduce((a, b) => (b.cm > a.cm ? b : a));
  return { cm: pick.cm, status: "ok", halfDrop, scaled };
}

/* ---------- maths (pure) ---------- */
const roundUpTo = (x, step) => (step > 0 ? Math.ceil(x / step - EPS) * step : x);
const ceilInt = (x) => Math.ceil(x - EPS);
const r1 = (x) => Math.round(x * 10) / 10;
function repeatPct(rcm, c) { if (!(rcm > 0)) return 0; if (rcm <= 34) return c.rep1; if (rcm <= 54) return c.rep2; if (rcm <= 69) return c.rep3; return c.rep4; }
function bandLabel(rcm) { if (!(rcm > 0)) return "plain"; if (rcm <= 34) return "up to 34 cm"; if (rcm <= 54) return "34–54 cm"; if (rcm <= 69) return "54–69 cm"; return "over 69 cm"; }

// Lay rectangular pieces across the fabric. Each piece: {w, h, n, label}. Pattern: centre the motif on every piece.
function layoutPieces(pieces, fab, c, L) {
  const usable = fab.widthCm - c.selvedge; let total = 0;
  for (const p of pieces) {
    if (!(p.n > 0)) continue;
    const effH = fab.vr > 0 ? roundUpTo(p.h, fab.vr) : p.h;
    let perRow, rows, len, widths = 1;
    if (p.w > usable) { // wider than the fabric: join widths
      widths = ceilInt(p.w / usable); rows = p.n; len = p.n * widths * effH;
      L.push(`${p.label}: ${L.f(p.w)} × ${L.f(p.h)} is wider than the fabric, so ${widths} widths joined × ${L.f(effH)}${p.n > 1 ? ` × ${p.n}` : ""} = ${L.f(len)}`);
    } else {
      // Pieces sit side by side; with a horizontal repeat each piece starts on the same point of the motif,
      // so piece-to-piece spacing is the piece width rounded up to whole repeats.
      const pitch = fab.hr > 0 ? roundUpTo(p.w, fab.hr) : p.w;
      perRow = Math.max(1, Math.floor((usable - p.w) / pitch + EPS) + 1);
      rows = ceilInt(p.n / perRow); len = rows * effH;
      L.push(`${p.label}: ${p.n} × (${L.f(p.w)} × ${L.f(p.h)})${effH !== p.h ? `, length rounded to the repeat ${L.f(effH)}` : ""}; ${perRow} across the width → ${rows} row${rows === 1 ? "" : "s"} = ${L.f(len)}`);
    }
    total += len;
  }
  return total;
}

function calcUpholstery(inp, fab, c, L) {
  const notes = []; let cm = 0; const rep = fab.vr > 0 ? fab.vr : fab.hr;
  const chart = (baseM, qty, label) => {
    const wf = Math.max(1, c.refWidth / fab.widthCm), pct = repeatPct(rep, c);
    const m = baseM * qty * wf * (1 + pct / 100);
    L.push(`${label}: chart ${r1(baseM)} m each for plain ${c.refWidth} cm fabric${qty > 1 ? ` × ${qty}` : ""}`);
    if (wf > 1.0001) L.push(`Narrower fabric (${L.fw(fab.widthCm)}): × ${wf.toFixed(2)}`);
    if (pct) L.push(`Pattern repeat ${L.f(rep)} (${bandLabel(rep)} band): + ${pct}%`);
    L.push(`= ${m.toFixed(2)} m`);
    return m * 100;
  };
  const q = Math.max(1, Math.round(inp.qty || 1));
  switch (inp.piece) {
    case "chair_seat": {
      const w = inp.w + 2 * c.seatWrap, h = inp.d + 2 * c.seatWrap;
      L.push(`Each seat cut: seat + ${L.f(c.seatWrap)} each side to wrap and staple`);
      cm = layoutPieces([{ w, h, n: q, label: "Seat covers" }], fab, c, L); break;
    }
    case "chair_back": cm = chart(c.mChairBack, q, "Dining chair, seat and back"); break;
    case "armchair": cm = chart(c.mArmchair, q, "Armchair / club chair"); break;
    case "wingback": cm = chart(c.mWingback, q, "Wingback chair"); break;
    case "sofa2": case "sofa3": {
      const two = inp.piece === "sofa2", base = two ? c.mSofa2 : c.mSofa3, ref = two ? c.sofa2Ref : c.sofa3Ref;
      const ratio = Math.min(2, Math.max(0.5, inp.len / ref)), k = c.sofaShare / 100, scale = 1 - k + k * ratio;
      L.push(`${two ? "2-seat" : "3-seat"} sofa: chart ${r1(base)} m for a ${L.f(ref)} sofa; yours is ${L.f(inp.len)}, so × ${scale.toFixed(2)} (${c.sofaShare}% of the fabric scales with length)`);
      cm = chart(base * scale, q, `${two ? "2-seat" : "3-seat"} sofa adjusted`); break;
    }
    case "ottoman": {
      const a = c.uphAllow, topW = inp.l + 2 * a, topH = inp.w + 2 * a, bh = inp.h + a + c.ottomanTuck;
      const boxLen = 2 * (inp.l + inp.w) + 4 * a, usable = fab.widthCm - c.selvedge, strips = ceilInt(boxLen / usable);
      L.push(`Top: ${L.f(inp.l)} × ${L.f(inp.w)} + ${L.f(a)} each side; sides: one ${L.f(boxLen)} strip, ${L.f(bh)} deep (height + seam + ${L.f(c.ottomanTuck)} tuck under)`);
      const top = layoutPieces([{ w: topW, h: topH, n: q, label: "Top" }], fab, c, L);
      const stripH = fab.vr > 0 ? roundUpTo(bh, fab.vr) : bh; const sides = q * strips * stripH;
      L.push(`Sides: ${strips} strip${strips === 1 ? "" : "s"} cut across the width × ${L.f(stripH)}${q > 1 ? ` × ${q}` : ""} = ${L.f(sides)}`);
      cm = top + sides; break;
    }
    case "headboard": {
      const e = c.hbWrap + c.uphAllow;
      L.push(`Headboard cut: width and height + ${L.f(e)} each side (padding wrap + staple allowance)`);
      cm = layoutPieces([{ w: inp.w + 2 * e, h: inp.h + 2 * e, n: q, label: "Headboard" }], fab, c, L); break;
    }
    case "cushions": {
      const s = c.cushionSeam, n = q * (inp.backs ? 2 : 1);
      L.push(`Each piece: cushion size + ${L.f(s)} seam allowance each side, ${inp.backs ? "front and back" : "front only"}${fab.vr > 0 || fab.hr > 0 ? ", pattern centred" : ""}`);
      cm = layoutPieces([{ w: inp.w + 2 * s, h: inp.h + 2 * s, n, label: inp.backs ? "Fronts and backs" : "Fronts" }], fab, c, L);
      notes.push("Piping, zips and inserts aren't included."); break;
    }
    default: return null;
  }
  if (fab.halfDrop && rep > 0) notes.push("This design has a half-drop repeat, which can need a little more for pattern matching.");
  return { cm, notes };
}

function calcRoman(inp, fab, c, L) {
  const notes = []; const inside = inp.mount !== "outside";
  const fw = inside ? inp.w - c.romanInsideDeduct : inp.w + 2 * c.romanOutsideSide;
  const fl = inside ? inp.d : inp.d + c.romanOutsideTop;
  if (inside) L.push(`Inside mount: finished ${L.f(fw)} wide (window − ${L.f(c.romanInsideDeduct)} clearance) × ${L.f(fl)} long`);
  else L.push(`Outside mount: finished ${L.f(fw)} wide (window + ${L.f(c.romanOutsideSide)} each side) × ${L.f(fl)} long (+ ${L.f(c.romanOutsideTop)} above the frame)`);
  const cutW = fw + c.romanSideHems, cutL0 = fl + c.romanHemHeader, cutL = fab.vr > 0 ? roundUpTo(cutL0, fab.vr) : cutL0;
  const usable = fab.widthCm - c.selvedge, widths = ceilInt(cutW / usable);
  L.push(`Cut width ${L.f(fw)} + ${L.f(c.romanSideHems)} side hems = ${L.f(cutW)} → ${widths} width${widths === 1 ? "" : "s"} of ${L.fw(usable)} usable fabric`);
  L.push(`Cut length ${L.f(fl)} + ${L.f(c.romanHemHeader)} hems and header = ${L.f(cutL0)}${cutL !== cutL0 ? `, rounded up to the ${L.f(fab.vr)} repeat = ${L.f(cutL)}` : ""}`);
  let cm = widths * cutL; L.push(`${widths} × ${L.f(cutL)} = ${L.f(cm)}`);
  cm += patternExtras(widths, fab, c, L);
  if (widths > 1) notes.push(`Your shade is wider than the fabric, so ${widths} widths will be seamed together. Your workroom will place the seams and match the pattern.`);
  notes.push("Lining isn't included.");
  return { cm, notes };
}

function calcDrapery(inp, fab, c, L) {
  const notes = []; const panels = inp.panels === 1 ? 1 : 2, f = inp.fullness || 2;
  const need = inp.w * f + c.drapeReturns * panels, usable = fab.widthCm - c.selvedge, widths = ceilInt(need / usable);
  L.push(`Track ${L.f(inp.w)} × ${f} fullness + ${L.f(c.drapeReturns)} returns/overlap × ${panels} panel${panels === 1 ? "" : "s"} = ${L.f(need)}`);
  const per = widths % 2 ? `${Math.floor(widths / 2) || ""}½` : String(widths / 2);
  L.push(`÷ ${L.fw(usable)} usable fabric width → ${widths} width${widths === 1 ? "" : "s"}${panels === 2 ? ` (${per} per panel)` : ""}`);
  const cut0 = inp.d + c.drapeHemHeading, cut = fab.vr > 0 ? roundUpTo(cut0, fab.vr) : cut0;
  L.push(`Cut drop ${L.f(inp.d)} + ${L.f(c.drapeHemHeading)} hems and heading = ${L.f(cut0)}${cut !== cut0 ? `, rounded up to the ${L.f(fab.vr)} repeat = ${L.f(cut)}` : ""}`);
  let cm = widths * cut; L.push(`${widths} × ${L.f(cut)} = ${L.f(cm)}`);
  cm += patternExtras(widths, fab, c, L);
  notes.push("Lining and interlining aren't included.");
  return { cm, notes };
}
function patternExtras(widths, fab, c, L) {
  let add = 0;
  if (fab.vr > 0 && fab.halfDrop && c.halfDropExtra && widths > 1) { const x = (widths - 1) * fab.vr / 2; add += x; L.push(`Half-drop match: + ½ repeat for each extra width = ${L.f(x)}`); }
  if (fab.vr > 0 && c.extraRepeat) { add += fab.vr; L.push(`One extra repeat to place the pattern = ${L.f(fab.vr)}`); }
  return add;
}

function finalise(res, c, v, vendorIsPJ) {
  const raw = res.cm / 100; let m = ceilInt(raw);
  const min = Math.max(1, v?.qmin || 1), inc = Math.max(1, v?.qinc || 1);
  if (m < min) m = min; if (inc > 1) m = Math.ceil(m / inc) * inc;
  return { raw, metres: Math.max(1, m), yards: r1(Math.max(1, m) / M_PER_YD), pj: vendorIsPJ && m < c.pjMin };
}

const API = { stripImperial, parseCm, parseWidth, parseRepeat, roundUpTo, repeatPct, layoutPieces, calcUpholstery, calcRoman, calcDrapery, finalise };
if (typeof window !== "undefined") window.JCFabricCalc = API;

/* ---------- UI ---------- */
const esc = (s) => String(s).replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]);
const PIECES = {
  chair_seat: { fields: ["w", "d", "qty"], labels: { w: "Seat width", d: "Seat depth", qty: "Number of chairs" } },
  chair_back: { fields: ["qty"], labels: { qty: "Number of chairs" } },
  armchair: { fields: ["qty"], labels: { qty: "Number of chairs" } },
  wingback: { fields: ["qty"], labels: { qty: "Number of chairs" } },
  sofa2: { fields: ["len"], labels: { len: "Sofa length (arm to arm)" } },
  sofa3: { fields: ["len"], labels: { len: "Sofa length (arm to arm)" } },
  ottoman: { fields: ["l", "w", "h"], labels: { l: "Length", w: "Width", h: "Height" } },
  headboard: { fields: ["w", "h"], labels: { w: "Headboard width", h: "Headboard height" } },
  cushions: { fields: ["w", "h", "qty", "backs"], labels: { w: "Cushion width", h: "Cushion height", qty: "Number of cushions" } },
};

/* ---------- Drawer scrolling (shared pattern with the fabric / mural calculators) ----------
   The theme's <modal-dialog> locks page scroll from its "opening" event, but that handler is not attached
   in some browsers (it is bound after the element moves itself into #moved-modal-elements, so Firefox
   never runs it). Then the mouse wheel scrolls the page behind the drawer. This keeps the drawer
   self-sufficient: it watches aria-hidden to lock/unlock page scroll itself, keeps wheel scrolling inside
   the drawer, and lifts the footer above Shopify's theme-preview bar. */
function jcDrawerScroll(modal, scroller, opts = {}) {
  if (!modal || !scroller || modal.dataset.jcScrollFix) return;
  modal.dataset.jcScrollFix = "1";
  if (!document.getElementById("jc-drawer-lock-css")) {
    const st = document.createElement("style"); st.id = "jc-drawer-lock-css";
    st.textContent = "html.jc-scroll-lock,html.jc-scroll-lock body{overflow:hidden!important;overscroll-behavior:none}html.jc-scroll-lock{scrollbar-gutter:stable}";
    document.head.appendChild(st);
  }
  const locks = (window.__jcDrawerLocks = window.__jcDrawerLocks || new Set());
  const isOpen = () => modal.isConnected && modal.getAttribute("aria-hidden") !== "true" && modal.hasAttribute("aria-modal");
  const previewBar = () => document.getElementById("PBarNextFrameWrapper") || document.getElementById("preview-bar-iframe");
  const updateInset = () => {
    let inset = 0; const pb = previewBar();
    if (pb) { const r = pb.getBoundingClientRect(), cs = getComputedStyle(pb); if (cs.display !== "none" && cs.visibility !== "hidden" && r.height > 0 && r.top < innerHeight) inset = Math.max(0, Math.min(200, innerHeight - r.top)); }
    modal.style.setProperty(opts.insetVar || "--jc-bottom-inset", Math.round(inset) + "px");
  };
  let wasOpen = false;
  const sync = () => {
    const open = isOpen();
    if (open === wasOpen) return;
    wasOpen = open;
    const key = modal.id || modal;
    if (open) { locks.add(key); document.documentElement.classList.add("jc-scroll-lock"); updateInset(); opts.onOpen && opts.onOpen(); }
    else { locks.delete(key); if (!locks.size) document.documentElement.classList.remove("jc-scroll-lock"); }
  };
  new MutationObserver(sync).observe(modal, { attributes: true, attributeFilter: ["aria-hidden", "aria-modal"] });
  sync();
  // Wheel / trackpad: scroll the drawer, never the page behind it.
  const doc = modal.querySelector('[role="document"]') || modal;
  modal.addEventListener("wheel", (e) => {
    if (!isOpen() || e.ctrlKey) return; // ctrl+wheel = browser zoom
    let dy = e.deltaY; if (!dy) return;
    if (e.deltaMode === 1) dy *= 16; else if (e.deltaMode === 2) dy *= scroller.clientHeight;
    // a scrollable element inside the drawer (e.g. a long list) that can still move gets the native scroll
    for (let n = e.target; n && n !== scroller && n !== modal && n.nodeType === 1; n = n.parentElement) {
      if (n.scrollHeight > n.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(n).overflowY) && (dy > 0 ? n.scrollTop < n.scrollHeight - n.clientHeight - 1 : n.scrollTop > 0)) return;
    }
    const max = scroller.scrollHeight - scroller.clientHeight;
    const can = dy > 0 ? scroller.scrollTop < max - 1 : scroller.scrollTop > 0;
    if (can && scroller.contains(e.target)) return; // normal native scroll of the drawer content
    e.preventDefault(); // would otherwise chain to the page
    if (can && doc.contains(e.target)) scroller.scrollTop += dy; // over the drawer header/footer: scroll the content
  }, { passive: false });
  window.addEventListener("resize", () => { if (isOpen()) updateInset(); }, { passive: true });
  const insetCbs = (window.__jcInsetCbs = window.__jcInsetCbs || new Set()); insetCbs.add(updateInset);
  const all = () => insetCbs.forEach((f) => f());
  const hookBar = () => {
    const pb = previewBar(); if (!pb) return false; if (pb.dataset.jcWatched) { updateInset(); return true; } pb.dataset.jcWatched = "1";
    if (window.ResizeObserver) { const ro = new ResizeObserver(all); ro.observe(pb); [...pb.children].forEach((c) => ro.observe(c)); }
    new MutationObserver(all).observe(pb, { attributes: true, subtree: true, attributeFilter: ["style", "class", "hidden"] });
    all(); return true;
  };
  if (!hookBar()) { let n = 0; const t = setInterval(() => { if (hookBar() || ++n > 20) clearInterval(t); }, 500); }
  // Sticky footer: it sits directly after the content and only pins to the bottom (with a divider)
  // while the content is taller than the drawer.
  const footer = opts.footer;
  if (footer) {
    let raf = 0;
    const update = () => {
      raf = 0;
      const overflow = scroller.scrollHeight > scroller.clientHeight + 1;
      const pinTo = scroller.getBoundingClientRect().bottom - (parseFloat(getComputedStyle(footer).bottom) || 0);
      const stuck = overflow && Math.abs(footer.getBoundingClientRect().bottom - pinTo) < 1.5;
      footer.classList.toggle("is-stuck", stuck);
    };
    const queue = () => { if (!raf) raf = requestAnimationFrame(update); };
    scroller.addEventListener("scroll", queue, { passive: true });
    window.addEventListener("resize", queue, { passive: true });
    if (window.ResizeObserver) { const ro = new ResizeObserver(queue); ro.observe(scroller); Array.from(scroller.children).forEach((c) => ro.observe(c)); }
    new MutationObserver(queue).observe(scroller, { childList: true, subtree: true, attributes: true, attributeFilter: ["hidden", "class", "style"] });
    new MutationObserver(queue).observe(modal, { attributes: true, attributeFilter: ["aria-hidden"] });
    queue();
  }
  updateInset();
  return { updateInset, isOpen };
}

class FabricCalculator {
  constructor(el) {
    this.el = el; this.q = (s) => el.querySelector(s); this.qa = (s) => [...el.querySelectorAll(s)];
    this.c = JSON.parse(this.q("[data-fc-config]").textContent || "{}");
    this.variants = JSON.parse(this.q("[data-fc-variants]").textContent || "[]");
    this.sizeIdx = parseInt(el.dataset.sizeIndex, 10); this.colourIdx = parseInt(el.dataset.colourIndex, 10); this.scaleIdx = parseInt(el.dataset.scaleIndex, 10);
    this.formId = el.dataset.formId; this.unit = "cm"; this.mode = this.q('[role="tab"][aria-selected="true"]')?.dataset.fcTab || "upholstery";
    this.vendorIsPJ = !!this.c.pjVendor && (el.dataset.vendor || "").trim().toLowerCase() === this.c.pjVendor.trim().toLowerCase();
    this.result = this.q("[data-fc-result]"); this.cta = this.q("[data-fc-add]"); this.pjBtn = this.q("[data-fc-add-min]"); this.sampleBtn = this.q("[data-fc-sample]");
    this.edited = false; this.loadSpec();
    this.qa("input,select").forEach((i) => i.addEventListener("input", () => this.calc()));
    this.qa("input[type=checkbox],input[type=radio],select").forEach((i) => i.addEventListener("change", () => this.calc()));
    this.qa("[data-fc-unit]").forEach((b) => b.addEventListener("click", () => this.setUnit(b.dataset.fcUnit)));
    this.qa('[role="tab"]').forEach((t) => { t.addEventListener("click", () => this.setMode(t.dataset.fcTab, true)); t.addEventListener("keydown", (e) => this.tabKey(e)); });
    this.q("[data-fc-piece]").addEventListener("change", () => this.showPieceFields());
    this.q("[data-fc-edit]").addEventListener("click", (e) => { e.preventDefault(); this.toggleEdit(); });
    this.q("[data-fc-form]").addEventListener("submit", (e) => e.preventDefault());
    this.cta.addEventListener("click", () => this.addToCart(this.rec?.metres, this.cta));
    this.pjBtn?.addEventListener("click", () => this.addToCart(this.c.pjMin, this.pjBtn));
    this.sampleBtn?.addEventListener("click", (e) => { e.preventDefault(); this.addSample(); });
    el.addEventListener("on:modal:opening", () => { document.body.classList.add("u-scroll-disabled"); if (!this.edited) this.loadSpec(); this.updateSampleLink(); this.calc(); });
    // Own scroll lock + wheel handling + preview-bar inset (the theme's opening event doesn't fire in every browser)
    this.drawer = jcDrawerScroll(el, this.q(".fabric-calc__scroll"), { footer: this.q(".fabric-calc__footer"), insetVar: "--fc-bottom-inset", onOpen: () => { if (!this.edited) this.loadSpec(); this.updateSampleLink(); this.calc(); } });
    this.setMode(this.mode, false); this.showPieceFields(); this.updateSampleLink(); this.calc();
  }
  /* --- spec from the product --- */
  currentVariant() {
    const form = document.getElementById(this.formId);
    const id = form ? parseInt(form.querySelector('input[name="id"]')?.value, 10) : null;
    return this.variants.find((v) => v.id === id) || this.variants[0] || null;
  }
  loadSpec() {
    const cur = this.currentVariant(), scale = cur && this.scaleIdx >= 0 ? cur.options[this.scaleIdx] : "";
    const w = parseWidth(this.el.dataset.width), vr = parseRepeat(this.el.dataset.vrepeat, scale), hr = parseRepeat(this.el.dataset.hrepeat, scale);
    this.spec = { widthCm: w ? w.cm : null, printed: !!w?.printed, vr: vr.cm, hr: hr.cm, vrStatus: vr.status, halfDrop: !!vr.halfDrop, scaled: vr.scaled || hr.scaled, scale };
    this.setLen(this.q("[data-fc-fab=width]"), w ? w.cm : NaN);
    this.setLen(this.q("[data-fc-fab=vr]"), vr.cm);
    this.setLen(this.q("[data-fc-fab=hr]"), hr.cm);
    this.q("[data-fc-fab=match]").value = vr.halfDrop ? "half" : "straight";
    const bits = [];
    if (!w) bits.push("We couldn't read this fabric's width, so please enter it.");
    if (w?.printed) bits.push("Uses the printed width.");
    if (vr.status === "tbc") bits.push("The pattern repeat hasn't been confirmed yet, so we've assumed no repeat. Check your sample, or ask us.");
    if (vr.scaled && scale) bits.push(`Repeat for the ${esc(scale.toLowerCase())}.`);
    this.q("[data-fc-spec-note]").innerHTML = bits.join(" ");
    this.q("[data-fc-spec-note]").hidden = !bits.length;
    if (!w && !this.editing) this.toggleEdit(true);
  }
  toggleEdit(force) {
    this.editing = force ?? !this.editing; this.edited = this.edited || this.editing;
    this.qa("[data-fc-fab]").forEach((i) => (i.disabled = !this.editing));
    this.q("[data-fc-edit]").textContent = this.editing ? "Done" : "Edit";
    this.q("[data-fc-edit]").setAttribute("aria-expanded", String(this.editing));
    this.q("[data-fc-auto-label]").textContent = this.editing ? "Enter your own fabric details" : "Filled in from this fabric";
    if (this.editing) this.q("[data-fc-fab=width]").focus();
    this.calc();
  }
  fabric() {
    const g = (k) => this.lenCm(this.q(`[data-fc-fab=${k}]`));
    const widthCm = g("width"); const vr = g("vr") || 0, hr = g("hr") || 0;
    return { widthCm, vr: vr > 0 ? vr : 0, hr: hr > 0 ? hr : 0, halfDrop: this.q("[data-fc-fab=match]").value === "half" };
  }
  /* --- units --- */
  setLen(i, cm) { i.value = isFinite(cm) ? this.toUnit(cm) : ""; i.dataset.cm = isFinite(cm) ? String(cm) : ""; i.dataset.shown = i.value; }
  lenCm(i) { if (i.dataset.cm && i.value === i.dataset.shown) return parseFloat(i.dataset.cm); return this.toCm(parseFloat(i.value)); }
  toCm(v) { return isFinite(v) ? (this.unit === "in" ? v * CM_PER_IN : v) : NaN; }
  toUnit(cm) { if (!isFinite(cm)) return ""; return String(this.unit === "in" ? r1(cm / CM_PER_IN) : r1(cm)); }
  setUnit(u) {
    if (u === this.unit) return;
    const vals = this.qa("[data-fc-len]").map((i) => [i, this.lenCm(i)]);
    this.unit = u; vals.forEach(([i, cm]) => this.setLen(i, cm));
    this.qa("[data-fc-unit]").forEach((b) => { const on = b.dataset.fcUnit === u; b.classList.toggle("is-on", on); b.setAttribute("aria-pressed", String(on)); });
    this.qa("[data-fc-u]").forEach((s) => (s.textContent = u));
    this.qa("[data-fc-len]").forEach((i) => { const ph = i.dataset["ph" + (u === "in" ? "In" : "Cm")]; if (ph) i.placeholder = ph; });
    this.calc();
  }
  /* --- modes --- */
  setMode(mode, focus) {
    this.mode = mode;
    this.qa('[role="tab"]').forEach((t) => { const on = t.dataset.fcTab === mode; t.setAttribute("aria-selected", String(on)); t.tabIndex = on ? 0 : -1; t.classList.toggle("is-on", on); if (on && focus) t.focus(); });
    this.qa("[data-fc-panel]").forEach((p) => (p.hidden = p.dataset.fcPanel !== mode));
    this.calc();
  }
  tabKey(e) {
    const tabs = this.qa('[role="tab"]'), i = tabs.indexOf(e.currentTarget); let n = null;
    if (e.key === "ArrowRight") n = (i + 1) % tabs.length; else if (e.key === "ArrowLeft") n = (i - 1 + tabs.length) % tabs.length; else if (e.key === "Home") n = 0; else if (e.key === "End") n = tabs.length - 1;
    if (n != null) { e.preventDefault(); this.setMode(tabs[n].dataset.fcTab, true); }
  }
  showPieceFields() {
    const piece = this.q("[data-fc-piece]").value, def = PIECES[piece] || { fields: [], labels: {} };
    this.qa("[data-fc-ufield]").forEach((f) => {
      const k = f.dataset.fcUfield; f.hidden = !def.fields.includes(k);
      const lbl = f.querySelector("[data-fc-lbl]"); if (lbl && def.labels[k]) lbl.textContent = def.labels[k];
    });
    const nl = this.q("[data-fc-piece-note]"), opt = this.q("[data-fc-piece]").selectedOptions[0];
    nl.textContent = opt?.dataset.note || ""; nl.hidden = !nl.textContent;
    this.calc();
  }
  val(name) { const i = this.q(`[data-fc-in="${name}"]`); if (!i) return NaN; const v = parseFloat(i.value); return i.hasAttribute("data-fc-len") ? this.lenCm(i) : v; }
  /* --- calculation --- */
  calc() {
    this.rec = null; this.cta.disabled = true; this.cta.textContent = "Add to cart"; if (this.pjBtn) this.pjBtn.hidden = true;
    const fab = this.fabric();
    if (!(fab.widthCm > 20)) return this.msg("Enter the fabric width to see how much you need.");
    const L = []; L.f = (cm) => (this.unit === "in" ? `${r1(cm / CM_PER_IN)} in` : `${r1(cm)} cm`); L.fw = L.f;
    const pos = (x) => isFinite(x) && x > 0; let res = null, need = null;
    if (this.mode === "upholstery") {
      const piece = this.q("[data-fc-piece]").value, def = PIECES[piece];
      const inp = { piece, w: this.val("w"), d: this.val("d"), h: this.val("h"), l: this.val("l"), len: this.val("len"), qty: this.val("qty") || 1, backs: this.q('[data-fc-in="backs"]').checked };
      const missing = def.fields.filter((k) => k !== "qty" && k !== "backs" && !pos(inp[k]));
      if (!def.fields.includes("qty")) inp.qty = 1;
      if (missing.length) need = "Enter your furniture's measurements to see how much you need.";
      else res = calcUpholstery(inp, fab, this.c, L);
    } else if (this.mode === "roman") {
      const inp = { w: this.val("rw"), d: this.val("rd"), mount: this.q('[data-fc-in="mount"]:checked')?.value || "inside" };
      if (!pos(inp.w) || !pos(inp.d)) need = "Enter your window's width and drop to see how much you need.";
      else if (inp.mount === "inside" && inp.w <= this.c.romanInsideDeduct) need = "Please check the window width.";
      else res = calcRoman(inp, fab, this.c, L);
    } else {
      const inp = { w: this.val("dw"), d: this.val("dd"), fullness: parseFloat(this.q('[data-fc-in="fullness"]').value) || 2, panels: this.q('[data-fc-in="panels"]:checked')?.value === "1" ? 1 : 2 };
      if (!pos(inp.w) || !pos(inp.d)) need = "Enter your track width and drop to see how much you need.";
      else res = calcDrapery(inp, fab, this.c, L);
    }
    if (need) return this.msg(need);
    if (!res) return this.msg("Something went wrong — please check your measurements.");
    const v = this.metreVariant(); const fin = finalise(res, this.c, v, this.vendorIsPJ); this.rec = fin;
    const notes = [...res.notes];
    if (this.mode === "upholstery") {
      if (this.el.dataset.upholsteryUse === "none") notes.unshift("This fabric is recommended for drapery and home goods rather than upholstery. Ask us if you're unsure.");
      else if (this.el.dataset.upholsteryUse === "light") notes.unshift("This fabric is suited to light upholstery, such as occasional chairs, headboards and cushions.");
      if (this.el.dataset.velvet === "true") notes.push("Velvet has a pile, so every piece must be cut the same way up.");
    }
    if (fin.raw > 0 && fin.metres > ceilInt(fin.raw)) notes.push(`Rounded up to this fabric's minimum order of ${fin.metres} m.`);
    if (fin.pj) notes.push(esc(this.c.pjNote));
    if (fin.metres >= 30) notes.push("For a project this size, contact us about project or trade pricing.");
    const yd = fin.yards.toFixed(1).replace(/\.0$/, "");
    this.result.innerHTML = `<div class="fabric-calc__card">
        <div class="fabric-calc__big">${fin.metres} m <span class="fabric-calc__alt">(${yd} yd)</span></div>
        <div class="fabric-calc__what">Order ${fin.metres} metre${fin.metres === 1 ? "" : "s"} of this fabric. Our calculation came to ${fin.raw.toFixed(2)} m, rounded up to whole metres.</div>
      </div>
      ${notes.length ? `<ul class="fabric-calc__notes">${notes.map((n) => `<li>${n}</li>`).join("")}</ul>` : ""}
      ${this.mode === "upholstery" ? `<p class="fabric-calc__uph-note" role="note">${esc(this.c.upholsteryNote)}</p>` : ""}
      <details class="fabric-calc__working"><summary>How we worked it out</summary><p>${L.map(esc).join("\n")}\nTotal ${fin.raw.toFixed(2)} m → ${fin.metres} m (${yd} yd)</p></details>`;
    this.cta.textContent = `Add ${fin.metres} m to cart`; this.cta.disabled = !v;
    if (!v) this.err("We couldn't find the per-metre option for this colour. Please choose it on the product page.");
    if (this.pjBtn && fin.pj) { this.pjBtn.hidden = false; this.pjBtn.textContent = `Add ${this.c.pjMin} m instead (avoids the surcharge)`; }
  }
  msg(t) { this.result.innerHTML = `<p class="fabric-calc__prompt">${esc(t)}</p>`; }
  err(t) { this.result.insertAdjacentHTML("beforeend", `<div class="fabric-calc__err" role="alert">${esc(t)}</div>`); }
  /* --- variants and cart --- */
  isSample(v) { return this.sizeIdx >= 0 && /sample/i.test(v.options[this.sizeIdx] || ""); }
  isMetre(v) { return this.sizeIdx < 0 ? !/sample/i.test(v.title) : /per\s*met|metre|^meter$/i.test(v.options[this.sizeIdx] || ""); }
  sameExceptSize(a, b) { return a.options.every((o, i) => i === this.sizeIdx || o === b.options[i]); }
  pick(pred) {
    const cur = this.currentVariant(); if (!cur) return null;
    if (pred(cur)) return cur;
    const same = this.variants.filter((v) => pred(v) && this.sameExceptSize(v, cur));
    const col = this.variants.filter((v) => pred(v) && (this.colourIdx < 0 || v.options[this.colourIdx] === cur.options[this.colourIdx]));
    return same.find((v) => v.available) || same[0] || col.find((v) => v.available) || col[0] || null;
  }
  metreVariant() { return this.pick((v) => this.isMetre(v)); }
  sampleVariant() { return this.pick((v) => this.isSample(v)); }
  updateSampleLink() { if (this.sampleBtn) this.sampleBtn.hidden = !this.sampleVariant(); }
  async addToCart(qty, btn) {
    const v = this.metreVariant(); if (!v || !qty) return;
    const txt = btn.textContent; btn.disabled = true; btn.textContent = "Adding…";
    try { await Cart.add({ id: v.id, quantity: qty }); btn.textContent = "Added"; await this.openCart(); }
    catch (e) { this.err(e?.message || "Sorry, that didn't add to your cart. Please try from the product page."); }
    finally { setTimeout(() => { btn.disabled = false; btn.textContent = txt; }, 1200); }
  }
  async addSample() {
    const v = this.sampleVariant(); if (!v) return; const b = this.sampleBtn, txt = b.textContent;
    try { b.textContent = "Adding sample…"; await Cart.add({ id: v.id, quantity: 1 }); b.textContent = "Sample added"; await this.openCart(); }
    catch (e) { this.err(e?.message || "Sorry, we couldn't add the sample. Please choose Sample on the product page."); }
    finally { setTimeout(() => { b.textContent = txt; }, 1500); }
  }
  async openCart() {
    const cart = document.querySelector(".modal--cart, .popup--cart-notification");
    await this.el.close?.();
    if (cart && !cart.shown && typeof cart.open === "function") cart.open(); else if (!cart) window.location.href = "/cart";
  }
}
function boot() { document.querySelectorAll("modal-dialog.fabric-calc:not([data-fc-ready])").forEach((el) => { el.dataset.fcReady = "1"; window.JCFabricCalcInstance = new FabricCalculator(el); }); }
boot();
document.addEventListener("click", (e) => {
  const a = e.target.closest('a[href$="#fabric-calculator"]');
  if (!a || a.hasAttribute("data-a11y-dialog-show")) return;
  const el = document.getElementById("fabric-calculator");
  if (el && typeof el.open === "function") { e.preventDefault(); el.open(); }
});
